import React from 'react';
import {
  Box,
  Container,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Outlet } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';

const DRAWER_WIDTH = 260;
const COLLAPSED_DRAWER_WIDTH = 65;

/**
 * Layout Component
 * 
 * Main application layout with responsive sidebar navigation and header.
 * Provides consistent structure across all authenticated pages with theme support.
 * 
 * Features:
 * - Responsive drawer (permanent on desktop, temporary on mobile)
 * - Collapsible sidebar on desktop for more content space
 * - Sticky header with navigation and user controls
 * - Theme-aware styling using CSS variables
 * - Full dark mode support
 * - Proper z-index layering for overlays
 * - Footer with copyright information
 */
const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const drawerWidth = collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'var(--color-background, #fafafa)',
      }}
    >
      {/* Header at the top with higher z-index */}
      <Box sx={{ position: 'relative', zIndex: theme.zIndex.appBar }}>
        <Header onMenuClick={handleDrawerToggle} />
      </Box>

      {/* Content area with drawer and main content side by side */}
      <Box sx={{ display: 'flex', flexGrow: 1, position: 'relative' }}>
        {/* Desktop drawer */}
        {!isMobile && (
          <Drawer
            variant="permanent"
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
                position: 'relative',
                border: 'none',
                borderRight: '1px solid var(--color-border, rgba(0, 0, 0, 0.12))',
                backgroundColor: 'var(--color-surface, #ffffff)',
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                overflowX: 'hidden',
              },
            }}
          >
            <Sidebar collapsed={collapsed} onToggleCollapse={handleToggleCollapse} />
          </Drawer>
        )}

        {/* Mobile drawer */}
        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
                backgroundColor: 'var(--color-surface, #ffffff)',
                borderRight: '1px solid var(--color-border, rgba(0, 0, 0, 0.12))',
              },
            }}
          >
            <Sidebar />
          </Drawer>
        )}

        {/* Main content area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--color-background, #fafafa)',
          }}
        >
          <Container
            maxWidth="xl"
            sx={{
              py: 3,
              px: { xs: 2, sm: 3 },
              flexGrow: 1,
            }}
          >
            <Outlet />
          </Container>

          {/* Footer */}
          <Box
            component="footer"
            sx={{
              py: 2,
              px: 2,
              textAlign: 'center',
              borderTop: '1px solid var(--color-border, rgba(0, 0, 0, 0.12))',
              backgroundColor: 'var(--color-surface, #ffffff)',
              color: 'var(--color-text-secondary, rgba(0, 0, 0, 0.6))',
              fontSize: '0.875rem',
            }}
          >
            © {new Date().getFullYear()} Surge. All rights reserved.
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;